export const featuresHub = {
  path: '/features',
  images: {
    hero: '/feature-pos.webp',
  },
  title: 'Point of sale system features for restaurants',
  description:
    'Everything in a modern POS system—take orders, seat guests, process payments, manage staff, and understand performance from one cloud platform.',
  seoTitle: 'POS System Features for Restaurants | Tillio POS',
  seoDescription:
    'Explore cloud POS system features for restaurants: point of sale, tables, payments, team management, menus, analytics, kitchen display, and customer-facing display.',
}

export const features = [
  {
    slug: 'point-of-sale',
    path: '/features/point-of-sale',
    title: 'Point of Sale',
    shortTitle: 'Point of Sale',
    hubDescription: 'Full-featured restaurant POS system with order processing and real-time updates across every device.',
    seoTitle: 'Point of Sale System for Restaurants | Tillio POS',
    seoDescription:
      'A modern point of sale system for restaurants. Tillio POS handles orders, tickets, and service on iPad—cloud-based and built for busy floors.',
    images: {
      hero: '/feature-pos.webp',
      card: '/feature-pos.webp',
    },
    sectionLayout: 'tabs',
    intro:
      'A restaurant POS system that takes orders, fires tickets, and keeps every station in sync—built for dining floors, not retail checkouts.',
    detailTabs: {
      title: 'Built for the dining floor',
      description:
        'Ring items, fire tickets, and keep every station aligned—without a clunky retail-style checkout.',
      tabs: [
        {
          label: 'Ordering',
          title: 'Orders that never get lost',
          description:
            'Send items to the right station the moment they’re ringed up. Servers stay on the floor while the kitchen gets clear, timed tickets.',
          bullets: [
            'Fast item search and category browsing',
            'Modifiers, notes, and course timing',
            'Real-time sync across tablets and stations',
            'Open tickets that stay organized through the shift',
          ],
          body: [
            'Every addition, void, and note stays attached to the ticket so FOH and BOH work from the same source of truth.',
          ],
        },
        {
          label: 'Speed',
          title: 'Built for service speed',
          description:
            'Large touch targets, clear order status, and workflows designed for busy lunch and dinner rushes.',
          bullets: [
            'Intuitive iPad-first interface',
            'Split checks and custom payments',
            'Works with tables, counters, and quick serve',
            'Customer-facing display for live order review',
          ],
        },
        {
          label: 'Tickets',
          title: 'Tickets that stay organized',
          description:
            'Keep open checks readable from open to close—whether you’re running one tablet or a room full of them.',
          bullets: [
            'Open tickets visible across devices',
            'Clear status as items move through service',
            'Less double-entry between stations',
            'Fewer lost chits during the rush',
          ],
        },
        {
          label: 'Service styles',
          title: 'One POS for how you actually serve',
          description:
            'Full-service, counter, or quick serve—Tillio adapts to your floor instead of forcing a retail flow.',
          bullets: [
            'Table-tied or walk-up ordering',
            'Shared menus across every device',
            'Pairs with payments, tables, and kitchen display',
            'Cloud updates without stopping service',
          ],
        },
      ],
    },
    sections: [],
    related: ['table-management', 'payments', 'customer-facing-display'],
  },
  {
    slug: 'table-management',
    path: '/features/table-management',
    title: 'Table Management',
    shortTitle: 'Tables',
    hubDescription: 'See every table’s status, seat guests faster, and keep orders tied to the right party.',
    seoTitle: 'Restaurant Table Management Software',
    seoDescription:
      'Manage seating, table status, and orders by table with Tillio POS. Custom naming, live floor visibility, and smoother turns.',
    images: {
      hero: '/feature-tables.webp',
      card: '/feature-tables.webp',
    },
    sectionLayout: 'tabs',
    intro:
      'Know which tables are open, seated, ordering, or ready to clear—so hosts and servers stay coordinated all night.',
    detailTabs: {
      title: 'Run the floor with clarity',
      description:
        'Seating, status, and open checks stay connected so hosts and servers aren’t guessing who’s where.',
      tabs: [
        {
          label: 'Floor view',
          title: 'Your floor at a glance',
          description:
            'Track table status in real time and organize the room the way your restaurant actually runs.',
          bullets: [
            'Live table status for the whole team',
            'Customizable table naming conventions',
            'Orders organized by table and party',
            'Faster turns with clearer handoffs',
          ],
        },
        {
          label: 'Seating',
          title: 'Seating that matches your service',
          description:
            'Whether you run a tight bistro or a multi-room dining room, Tillio keeps seating and tickets connected.',
          bullets: [
            'Support for dine-in workflows',
            'Combine or move parties with less chaos',
            'Clear ownership of open checks',
            'Works alongside POS and kitchen display',
          ],
        },
        {
          label: 'Parties',
          title: 'Keep every party on the right ticket',
          description:
            'Open checks stay tied to tables and parties so servers aren’t hunting for the wrong order mid-rush.',
          bullets: [
            'Orders linked to the right table',
            'Less miscommunication between host and floor',
            'Smoother handoffs when sections change',
            'Clearer path from seat to check close',
          ],
        },
        {
          label: 'Turns',
          title: 'Turn tables with less friction',
          description:
            'When everyone can see what’s open, seated, or ready to clear, turns get faster without rushing guests.',
          bullets: [
            'Status visibility for hosts and servers',
            'Fewer empty tables sitting unnoticed',
            'Cleaner close-out when the party is done',
            'Better coordination on busy nights',
          ],
        },
      ],
    },
    sections: [],
    related: ['point-of-sale', 'team-management', 'kitchen-display'],
  },
  {
    slug: 'payments',
    path: '/features/payments',
    title: 'Payment Processing',
    shortTitle: 'Payments',
    hubDescription: 'Secure Stripe-powered payments with chip, swipe, and tap—right inside your POS.',
    seoTitle: 'Restaurant Payment Processing with Stripe',
    seoDescription:
      'Accept cards with Stripe Terminal, manage disputes, and keep checkout fast with Tillio POS payment processing.',
    images: {
      hero: '/feature-payments.webp',
      card: '/feature-payments.webp',
    },
    sectionLayout: 'tabs',
    intro:
      'Process payments without juggling a separate app. Tillio pairs your floor workflow with Stripe’s secure payment stack.',
    detailTabs: {
      title: 'Checkout without leaving the POS',
      description:
        'Card present payments, tips, splits, and receipts stay tied to the ticket—powered by Stripe.',
      tabs: [
        {
          label: 'Card present',
          title: 'Checkout guests trust',
          description:
            'Chip, swipe, and contactless payments with end-to-end encryption and PCI-ready processing through Stripe.',
          bullets: [
            '1.1% Tillio flat rate + Stripe processing fees',
            'Stripe Terminal reader support',
            'Split bills and tip workflows',
            'Dispute management tools',
          ],
        },
        {
          label: 'Tied to the order',
          title: 'Payments tied to the order',
          description:
            'No double entry. Totals flow from the ticket to the reader so staff close checks accurately and quickly.',
          bullets: [
            'Real-time transaction tracking',
            'Digital and printed receipt options',
            'Secure card data handling via Stripe',
            'Works at the table or the counter',
          ],
        },
        {
          label: 'Tips & splits',
          title: 'Tips and split checks that keep the line moving',
          description:
            'Support the tip and split workflows restaurants actually use—without jumping into another payments app.',
          bullets: [
            'Tip collection at checkout',
            'Split bills across cards or guests',
            'Customer-facing prompts when a display is paired',
            'Clear totals before the charge goes out',
          ],
        },
        {
          label: 'Stripe',
          title: 'Your Stripe account, restaurant checkout',
          description:
            'Connect the Stripe account you already have. Payouts, disputes, and card security stay with Stripe; Tillio runs the floor.',
          bullets: [
            'Connect an existing Stripe account',
            'Terminal readers for in-person payments',
            'No Tillio-held card funds',
            'Guides for setup and payment methods',
          ],
          body: [
            'Tillio is the restaurant front of house. Stripe remains your payments backbone for processing and settlement.',
          ],
        },
      ],
    },
    sections: [],
    related: ['point-of-sale', 'analytics', 'customer-facing-display'],
    resourceLinks: [
      { path: '/stripe', label: 'Tillio + Stripe' },
      { path: '/how-it-works', label: 'How it works' },
      { path: '/payment-methods', label: 'Payment methods' },
      { path: '/card-readers', label: 'Card readers' },
      { path: '/guides/connect-stripe-account', label: 'Connect Stripe guide' },
      { path: '/compare/stripe', label: 'Tillio vs Stripe Dashboard' },
    ],
  },
  {
    slug: 'team-management',
    path: '/features/team-management',
    title: 'Team Management',
    shortTitle: 'Team',
    hubDescription: 'Roles, PINs, permissions, and time tracking so every shift runs with the right access.',
    seoTitle: 'Restaurant Team Management & Time Tracking',
    seoDescription:
      'Manage staff roles, PIN login, permissions, and clock in/out with Tillio POS team management tools.',
    images: {
      hero: '/feature-team.webp',
      card: '/feature-team.webp',
    },
    sectionLayout: 'tabs',
    intro:
      'Give each role the access they need—and keep hours accurate—without another scheduling spreadsheet.',
    detailTabs: {
      title: 'Staff access and hours in one place',
      description:
        'Roles, PINs, and time tracking live next to the POS your team already uses every shift.',
      tabs: [
        {
          label: 'Roles',
          title: 'Staff profiles that fit your floor',
          description:
            'Create users with roles and permissions so servers, managers, and admins see only what they should.',
          bullets: [
            'Role-based access control',
            'Unique 4-digit PIN login per staff member',
            'Manager overrides when you need them',
            'Centralized staff directory per location',
          ],
        },
        {
          label: 'PIN login',
          title: 'Fast, accountable sign-in',
          description:
            'Staff jump in with a PIN instead of shared logins—so actions stay tied to the right person.',
          bullets: [
            'Per-person PIN access on device',
            'Quick switch between staff mid-shift',
            'Clearer accountability on voids and discounts',
            'Less shared-password chaos at the register',
          ],
        },
        {
          label: 'Time tracking',
          title: 'Time tracking built in',
          description:
            'Clock in and out from the same system your team already uses to take orders.',
          bullets: [
            'Clock in/out on device',
            'Hour totals for payroll prep',
            'Visibility into who is on shift',
            'Fewer tools for managers to juggle',
          ],
        },
        {
          label: 'Managers',
          title: 'Manager controls when it matters',
          description:
            'Give managers the overrides they need without opening every permission to the whole floor.',
          bullets: [
            'Elevated access for managers and admins',
            'Protect sensitive actions behind roles',
            'Keep servers focused on service tools',
            'One staff directory to maintain',
          ],
        },
      ],
    },
    sections: [],
    related: ['point-of-sale', 'analytics', 'menu-management'],
  },
  {
    slug: 'menu-management',
    path: '/features/menu-management',
    title: 'Menu Management',
    shortTitle: 'Menu',
    hubDescription: 'Update items, categories, prices, and availability across every device in real time.',
    seoTitle: 'Restaurant Menu Management Software',
    seoDescription:
      'Build and update menus, categories, pricing, and item availability in real time with Tillio POS.',
    images: {
      hero: '/feature-menu.webp',
      card: '/feature-menu.webp',
    },
    sectionLayout: 'tabs',
    intro:
      'Change a price at lunch and every tablet knows by dinner. Keep your menu accurate without reprinting chaos.',
    detailTabs: {
      title: 'Menus that stay accurate all day',
      description:
        'Organize items once, push updates everywhere, and handle specials without stopping service.',
      tabs: [
        {
          label: 'Structure',
          title: 'Menus that stay current',
          description:
            'Organize categories, items, descriptions, and images from one place and push updates instantly.',
          bullets: [
            'Categories and item organization',
            'Pricing and availability controls',
            'Photos and descriptions for staff clarity',
            'Real-time sync to all POS devices',
          ],
        },
        {
          label: 'Specials',
          title: 'Built for daily specials',
          description:
            '86 an item, launch a special, or adjust modifiers without stopping service.',
          bullets: [
            'Quick 86 / sellout updates',
            'Modifiers for customizations',
            'Consistent menus across locations',
            'Fewer mistakes at the register',
          ],
        },
        {
          label: 'Modifiers',
          title: 'Modifiers guests and cooks both understand',
          description:
            'Build the options your kitchen can execute and your servers can ring without ambiguity.',
          bullets: [
            'Customizations attached to the right items',
            'Clearer tickets for the line',
            'Fewer “what did they mean?” moments',
            'Consistent ringing across the team',
          ],
        },
        {
          label: 'Sync',
          title: 'Every tablet sees the same menu',
          description:
            'Update once and let cloud sync carry pricing and availability to every station on the floor.',
          bullets: [
            'No reprinting for every price change',
            'Availability updates mid-service',
            'Shared menu truth across devices',
            'Less training on “which menu is current?”',
          ],
        },
      ],
    },
    sections: [],
    related: ['point-of-sale', 'analytics', 'kitchen-display'],
  },
  {
    slug: 'analytics',
    path: '/features/analytics',
    title: 'Analytics & Reports',
    shortTitle: 'Analytics',
    hubDescription: 'Sales, item performance, and operational reports so you can manage with numbers—not guesswork.',
    seoTitle: 'Restaurant POS Analytics & Sales Reports',
    seoDescription:
      'Track sales, bestsellers, and performance metrics in real time with Tillio POS analytics and reporting.',
    images: {
      hero: '/feature-analytics.webp',
      card: '/feature-analytics.webp',
    },
    sectionLayout: 'tabs',
    intro:
      'See what sold, when it sold, and how the shift performed—so you can staff, stock, and price with confidence.',
    detailTabs: {
      title: 'Numbers you can act on',
      description:
        'Sales, items, and shift performance in one place—without exporting five disconnected systems.',
      tabs: [
        {
          label: 'Sales',
          title: 'Reports that answer real questions',
          description:
            'From daily sales to item-level performance, Tillio puts the numbers owners and managers actually use.',
          bullets: [
            'Sales summaries and trends',
            'Best-selling items and categories',
            'Staff and shift visibility',
            'Access from anywhere via web admin',
          ],
        },
        {
          label: 'Items',
          title: 'Know what actually sells',
          description:
            'Spot bestsellers, slow movers, and category performance so menu and prep decisions aren’t a guess.',
          bullets: [
            'Item and category performance',
            'Clearer signal for 86 and prep planning',
            'Support for pricing and mix decisions',
            'Less reliance on gut feel after close',
          ],
        },
        {
          label: 'Shifts',
          title: 'See how the shift performed',
          description:
            'Check progress mid-service or review the night after close—without waiting on a separate BI export.',
          bullets: [
            'Real-time performance views',
            'Shift-level visibility for managers',
            'Pairs with payments and menu data',
            'Faster close-out conversations',
          ],
        },
        {
          label: 'Decisions',
          title: 'Decisions without the spreadsheet scramble',
          description:
            'Pull what you need after close or mid-service—without exporting five different systems.',
          bullets: [
            'Multi-location friendly reporting',
            'Clear paths from insight to action',
            'One platform for ops and sales data',
            'Less time stitching CSVs together',
          ],
        },
      ],
    },
    sections: [],
    related: ['payments', 'menu-management', 'team-management'],
  },
  {
    slug: 'kitchen-display',
    path: '/features/kitchen-display',
    title: 'Kitchen Display System',
    shortTitle: 'Kitchen Display',
    hubDescription: 'Send tickets to the line digitally—faster, clearer, and with less paper clutter.',
    seoTitle: 'Restaurant Kitchen Display System (KDS)',
    seoDescription:
      'Route orders to kitchen displays with Tillio POS. Faster ticket flow, fewer errors, and a cleaner expo station.',
    images: {
      hero: '/feature-kds.webp',
      card: '/feature-kds.webp',
    },
    sectionLayout: 'tabs',
    intro:
      'Give the kitchen a clear queue of what to cook next. Less paper, fewer lost tickets, smoother expo.',
    detailTabs: {
      title: 'Digital tickets for the line',
      description:
        'Orders land on kitchen displays with the modifiers and notes cooks need—without a stack of paper chits.',
      tabs: [
        {
          label: 'Tickets',
          title: 'Tickets the line can trust',
          description:
            'Orders appear as they’re fired from the POS, with the modifiers and notes cooks need.',
          bullets: [
            'Automatic routing from POS tickets',
            'Clear modifiers and special instructions',
            'Less reliance on paper chits',
            'Faster communication between FOH and BOH',
          ],
        },
        {
          label: 'Expo',
          title: 'A cleaner expo station',
          description:
            'Keep the pass organized with a digital queue instead of lost tickets and handwritten corrections.',
          bullets: [
            'Visible queue of what’s next',
            'Fewer lost or unread chits',
            'Clearer handoffs at the pass',
            'Less clutter during the rush',
          ],
        },
        {
          label: 'Hardware',
          title: 'Built for restaurant hardware',
          description:
            'Pair kitchen displays with your Tillio command station and printers for a complete back-of-house setup.',
          bullets: [
            'Works with modular restaurant hardware',
            'Supports busy multi-station kitchens',
            'Complements table and order management',
            'Scales from cafés to full-service rooms',
          ],
        },
        {
          label: 'FOH ↔ BOH',
          title: 'Front and back stay aligned',
          description:
            'What servers ring is what the kitchen sees—so modifiers, notes, and timing don’t get lost in translation.',
          bullets: [
            'Direct flow from POS to display',
            'Fewer verbal corrections mid-ticket',
            'Better coordination on course timing',
            'Pairs with tables and menu updates',
          ],
        },
      ],
    },
    sections: [],
    related: ['point-of-sale', 'table-management', 'customer-facing-display'],
  },
  {
    slug: 'customer-facing-display',
    path: '/features/customer-facing-display',
    title: 'Customer-Facing Display',
    shortTitle: 'Customer Display',
    hubDescription:
      'Show guests their order, totals, tip options, and payment status on a second screen—live as you ring them up.',
    seoTitle: 'Customer-Facing Display for Restaurant POS | Tillio',
    seoDescription:
      'Tillio’s customer-facing display shows live cart items, totals, tip prompts, and payment confirmation so guests see every step of checkout.',
    images: {
      hero: '/promo-cfd.jpg',
      card: '/promo-cfd.jpg',
    },
    sectionLayout: 'tabs',
    intro:
      'Turn a second tablet into a guest-facing screen. As items hit the ticket, guests see the same order, totals, and prompts—building trust at the counter and at the table.',
    detailTabs: {
      title: 'Everything guests see at checkout',
      description:
        'The customer-facing display stays in sync with your POS so guests can follow the ticket, tip, and payment without guessing.',
      tabs: [
        {
          label: 'Live order',
          title: 'Orders guests can follow in real time',
          description:
            'As soon as you add, edit, or remove an item on the POS, the guest screen updates. No flipping the register, no surprise totals at the end.',
          bullets: [
            'Line items appear the moment they’re rung up',
            'Modifiers and special instructions stay visible',
            'Removed defaults and options display clearly',
            'Cart clears when the check closes or resets',
          ],
          body: [
            'Guests see the same ticket your staff is building—so questions about “what am I being charged for?” get answered before payment, not after.',
          ],
        },
        {
          label: 'Totals',
          title: 'Subtotal, tax, and total from the POS',
          description:
            'The display never invents numbers. Totals come straight from the POS so the guest screen and the register always match.',
          bullets: [
            'Live subtotal as the cart changes',
            'Tax and tax rate from your POS settings',
            'Grand total ready before tip or payment',
            'No double entry or manual recalculation on the guest screen',
          ],
        },
        {
          label: 'Tips',
          title: 'Tip prompts on the guest screen',
          description:
            'When it’s time to tip, the customer-facing display shows your configured tip options so guests can choose without staff turning a tablet around.',
          bullets: [
            'Percentage options from your tipping settings',
            'Round-up and custom tip flows when configured',
            'Clear base amount before tip is applied',
            'Prompt dismisses cleanly when checkout moves on',
          ],
          body: [
            'Tip selection happens on the guest-facing device, then syncs back to the POS so checkout stays fast and consistent.',
          ],
        },
        {
          label: 'Payments',
          title: 'Payment status guests can read',
          description:
            'Walk guests through tap, insert, or PIN prompts and show success or failure clearly—so nobody is left staring at a blank counter.',
          bullets: [
            'On-screen payment instructions during card present checkout',
            'Amount shown alongside payment guidance',
            'Success confirmation when the charge goes through',
            'Readable failure messaging when something needs a retry',
          ],
        },
        {
          label: 'Receipts',
          title: 'Receipt choice after payment',
          description:
            'After a successful payment, guests can choose how they want their receipt—without interrupting the next ticket on the POS.',
          bullets: [
            'Receipt prompt on the customer display',
            'Digital or printed options based on your setup',
            'Fast selection so the line keeps moving',
            'Prompt clears when the guest is done',
          ],
        },
        {
          label: 'Setup',
          title: 'Pair a second tablet to your POS',
          description:
            'Run the customer-facing display on a dedicated tablet near the counter or stand. It discovers and connects to your Tillio POS over the local network.',
          bullets: [
            'Works on a second iPad or tablet as the guest screen',
            'Pairs to your POS device for live cart updates',
            'Shows your restaurant name and logo when available',
            'Fits swivel stands built for guest-facing checkout',
          ],
          body: [
            'Use a stand that can face the guest, keep the POS facing staff, and let Tillio keep both screens in sync through the shift.',
          ],
        },
      ],
    },
    sections: [],
    related: ['point-of-sale', 'payments', 'table-management'],
    resourceLinks: [
      { path: '/hardware', label: 'Hardware & stands' },
      { path: '/features/payments', label: 'Payments' },
      { path: '/guides/tips-gratuity', label: 'Tips & gratuity guide' },
    ],
  },
]

export function getFeature(slug) {
  return features.find((f) => f.slug === slug)
}

export function getRelatedFeatures(slugs = []) {
  return slugs.map((slug) => getFeature(slug)).filter(Boolean)
}
